import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { eventKeys } from './useEvents';
import type { Event, EventFormValues, AttendeeStatus } from '../types';

// ── Create ─────────────────────────────────────────────────────────────────────

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: EventFormValues) => {
      const { data: { user } } = await supabase.auth.getUser();
      const payload = {
        title:            values.title.trim(),
        description:      values.description.trim() || null,
        type:             values.type,
        status:           values.status,
        location_name:    values.location_name.trim() || null,
        location_address: values.location_address.trim() || null,
        starts_at:        values.starts_at,
        ends_at:          values.ends_at || null,
        notes:            values.notes.trim() || null,
        color:            values.color,
        created_by:       user?.id ?? null,
      };
      const { data, error } = await (supabase as any)
        .from('events')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data as Event;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

// ── Update ─────────────────────────────────────────────────────────────────────

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: Partial<EventFormValues> }) => {
      const payload: Record<string, unknown> = {};
      if (values.title            !== undefined) payload.title            = values.title.trim();
      if (values.description      !== undefined) payload.description      = values.description.trim() || null;
      if (values.type             !== undefined) payload.type             = values.type;
      if (values.status           !== undefined) payload.status           = values.status;
      if (values.location_name    !== undefined) payload.location_name    = values.location_name.trim() || null;
      if (values.location_address !== undefined) payload.location_address = values.location_address.trim() || null;
      if (values.starts_at        !== undefined) payload.starts_at        = values.starts_at;
      if (values.ends_at          !== undefined) payload.ends_at          = values.ends_at || null;
      if (values.notes            !== undefined) payload.notes            = values.notes.trim() || null;
      if (values.color            !== undefined) payload.color            = values.color;

      const { data, error } = await (supabase as any)
        .from('events')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as Event;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
      qc.setQueryData(eventKeys.detail(data.id), data);
    },
  });
}

// ── Status change ──────────────────────────────────────────────────────────────

export function useUpdateEventStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Event['status'] }) => {
      const { data, error } = await (supabase as any)
        .from('events')
        .update({ status })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as Event;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
      qc.setQueryData(eventKeys.detail(data.id), data);
    },
  });
}

// ── Delete ─────────────────────────────────────────────────────────────────────

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('events')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

// ── Attendees ──────────────────────────────────────────────────────────────────

export function useAddAttendee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ event_id, contact_id }: { event_id: string; contact_id: string }) => {
      const { data, error } = await (supabase as any)
        .from('event_attendees')
        .insert({ event_id, contact_id })
        .select(`*, contact:contacts(id, full_name, phone, category)`)
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, { event_id }) => {
      qc.invalidateQueries({ queryKey: eventKeys.attendees(event_id) });
    },
  });
}

export function useUpdateAttendeeStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      event_id: _event_id,
      status,
    }: {
      id: string;
      event_id: string;
      status: AttendeeStatus;
    }) => {
      const { data, error } = await (supabase as any)
        .from('event_attendees')
        .update({ status, responded_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, { event_id }) => {
      qc.invalidateQueries({ queryKey: eventKeys.attendees(event_id) });
    },
  });
}

export function useRemoveAttendee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, event_id }: { id: string; event_id: string }) => {
      const { error } = await (supabase as any)
        .from('event_attendees')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { id, event_id };
    },
    onSuccess: (_data, { event_id }) => {
      qc.invalidateQueries({ queryKey: eventKeys.attendees(event_id) });
    },
  });
}
